import { useEffect, useMemo, useState } from 'react';
import { useAdminApi } from '../../../lib/adminApi';
import { sendCampaignInBatches, describeSendResult } from '../../../lib/mailingSend';
import MailingStatus from './MailingStatus';

// Search helper: case- and accent-insensitive ("harter" finds "Härter"),
// every word must appear in name or email
const normalize = (v) => String(v || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
const matchesSearch = (r, q) => {
  const words = normalize(q).split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const hay = normalize(`${r.firstName || ''} ${r.lastName || ''} ${r.email || ''}`);
  return words.every(w => hay.includes(w));
};

function SearchInput({ value, onChange }) {
  return (
    <div className="position-relative" style={{ maxWidth: 280, flex: '1 1 220px' }}>
      <input
        type="search"
        className="form-control"
        placeholder="Suche: Name oder E-Mail…"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Suche nach Name oder E-Mail"
      />
    </div>
  );
}

export default function BookingsAdmin() {
  const [tab, setTab] = useState('list'); // 'list' | 'emails' | 'status'
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <div className="p-3">
      {/* Tabs row + refresh icon on the right */}
      <div className="d-flex align-items-center justify-content-between">
        <ul className="nav nav-tabs">
          <li className="nav-item">
            <button className={`nav-link ${tab==='list'?'active':''}`} onClick={()=>setTab('list')}>
              Buchungen
            </button>
          </li>
          <li className="nav-item">
            <button className={`nav-link ${tab==='emails'?'active':''}`} onClick={()=>setTab('emails')}>
              Mailings
            </button>
          </li>
          <li className="nav-item">
            <button className={`nav-link ${tab==='status'?'active':''}`} onClick={()=>setTab('status')}>
              Versand-Status
            </button>
          </li>
        </ul>

        {/* Refresh icon */}
<button
  className="admin-refresh-btn"
  title="Neu laden"
  onClick={() => setRefreshKey(k => k + 1)}
  aria-label="Neu laden"
>
  <i className="lni-reload" />
</button>
      </div>

      <div className="mt-3">
        {tab === 'list'   && <BookingsTable refreshKey={refreshKey} />}
        {tab === 'emails' && <EmailRecipients refreshKey={refreshKey} />}
        {tab === 'status' && <MailingStatus refreshKey={refreshKey} />}
      </div>
    </div>
  );
}

function BookingsTable({ refreshKey = 0 }) {
  const api = useAdminApi();
  const [years, setYears] = useState([]);
  const [year, setYear] = useState('');
  const [day, setDay] = useState('all');
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [delModal, setDelModal] = useState({ show:false, id:null, name:'', busy:false, error:'' });

  useEffect(()=>{
    let alive = true;
    (async ()=>{
      try {
        const yrs = await api.get('/bookings/years');
        if (!alive) return;
        setYears(yrs);
        const defaultYear = (yrs && yrs.length) ? String(yrs[0]) : '';
        setYear(defaultYear);
      } catch(e) {
        setErr(String(e?.message||e));
      }
    })();
    return ()=>{alive=false};
  },[]); // eslint-disable-line

  useEffect(()=>{
    if (year===undefined) return;
    let alive = true;
    (async ()=>{
      setLoading(true); setErr('');
      try {
        const parts = [];
        if (year) parts.push(`year=${encodeURIComponent(year)}`);
        if (day && day !== 'all') parts.push(`day=${encodeURIComponent(day)}`);
        const qs = parts.length ? `?${parts.join('&')}` : '';
        const data = await api.get(`/bookings${qs}`);
        if (!alive) return;
        setRows(data||[]);
      } catch(e) {
        setErr(String(e?.message||e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return ()=>{alive=false};
 },[year, day, refreshKey]); // eslint-disable-line

  const visibleRows = useMemo(
    () => rows.filter(r => matchesSearch(r, search)),
    [rows, search]
  );

  const grandTotal = useMemo(
    () => visibleRows.reduce((sum, r)=> sum + (r?.grandTotal||0), 0),
    [visibleRows]
  );

  const openDelete = (row) => {
    setDelModal({ show:true, id: row.id, name: `${row.firstName} ${row.lastName}`, busy:false, error:'' });
  };
  const closeDelete = () => setDelModal({ show:false, id:null, name:'', busy:false, error:'' });

  const confirmDelete = async () => {
    if (!delModal.id) return;
    try {
      setDelModal(m => ({...m, busy:true, error:''}));
      await api.del(`/bookings/${delModal.id}`);
      setRows(list => list.filter(r => r.id !== delModal.id));
      closeDelete();
    } catch (e) {
      setDelModal(m => ({...m, busy:false, error: String(e?.message||e)}));
    }
  };

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <label className="form-label m-0 me-2">Jahr</label>
        <select className="form-select" style={{maxWidth: 200}}
          value={year} onChange={e=>setYear(e.target.value)}>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>

        <label className="form-label m-0 ms-3 me-2">Tag</label>
        <select className="form-select" style={{maxWidth: 240}}
            value={day} onChange={e=>setDay(e.target.value)}>
            <option value="all">Alle Tage</option>
            <option value="d1">Tag 1</option>
            <option value="d2">Tag 2</option>
            <option value="d3">Tag 3</option>
            <option value="combo">Kombiticket</option>
        </select>

        <SearchInput value={search} onChange={setSearch} />

        <div className="ms-auto small text-muted">
          {search.trim() ? `${visibleRows.length} von ${rows.length}` : rows.length} Einträge • Summe: €{grandTotal.toFixed(2)}
        </div>
      </div>

      {err && <div className="alert alert-danger">{err}</div>}
      {loading ? <Spinner/> : (
        
        <div className="table-responsive" style={{maxHeight: 400, overflowY:'auto', border:'1px solid #eee', borderRadius:8}}>
          <table className="table table-sm align-middle">
            <thead>
              <tr>
                <th>Datum</th>
                <th>Name</th>
                <th>Email</th>
                <th>D1</th>
                <th>D2</th>
                <th>D3</th>
                <th>Kombi</th>
                <th>Student</th>
                <th>Tag-Preis</th>
                <th>Kombi-Preis</th>
                <th>Total</th>
                <th style={{width:80}}>...</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map(r=>(
                <tr key={r.id}>
                  <td>{new Date(r.insertedIn).toLocaleString("de-DE", {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}</td>
                  <td>{r.firstName} {r.lastName}</td>
                  <td>{r.email}</td>
                  <td>{r.day1TicketsAmount === 0 ? '-' : r.day1TicketsAmount}</td>
                  <td>{r.day2TicketsAmount === 0 ? '-' : r.day2TicketsAmount}</td>
                  <td>{r.day3TicketsAmount === 0 ? '-' : r.day3TicketsAmount}</td>
                  <td>{r.allDaysTicketsAmount === 0 ? '-' : r.allDaysTicketsAmount}</td>
                  <td>{r.isStudent ? '✓' : ''}</td>
                  <td>€{(r.appliedDayPrice||0).toFixed?.(2) ?? Number(r.appliedDayPrice||0).toFixed(2)}</td>
                  <td>€{(r.comboPrice||0).toFixed?.(2) ?? Number(r.comboPrice||0).toFixed(2)}</td>
                  <td><strong>€{(r.grandTotal||0).toFixed?.(2) ?? Number(r.grandTotal||0).toFixed(2)}</strong></td>
                   <td>
                        <button
                        type="button"
                        className="icon-btn icon-btn-danger"
                        onClick={() => openDelete(r)}
                        title="Entfernen">
                            <i className="lni-trash" aria-hidden="true"></i>
                        </button>
                  </td>
                </tr>
              ))}
              {!visibleRows.length && (
                <tr><td colSpan="12" className="text-center text-muted py-4">
                  {rows.length ? 'Keine Treffer für diese Suche.' : 'Keine Buchungen gefunden.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

   {/* Delete confirm modal */}
      {delModal.show && (
        <>
          <div className="modal fade show" style={{display:'block'}} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Reservierung löschen</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={closeDelete}></button>
                </div>
                <div className="modal-body">
                  <p>Möchten Sie die Reservierung von <strong>{delModal.name}</strong> wirklich löschen?</p>
                  {delModal.error && <div className="alert alert-danger mt-2 mb-0">{delModal.error}</div>}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" disabled={delModal.busy} onClick={closeDelete}>Abbrechen</button>
                  <button type="button" className="btn btn-danger" disabled={delModal.busy} onClick={confirmDelete}>
                    {delModal.busy ? 'Lösche…' : 'Löschen'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  );
}

function EmailRecipients({ refreshKey = 0 }) {
  const api = useAdminApi();

  // existing filter: all|subscribed|unsubscribed (server-side)
  const [filter, setFilter] = useState('subscribed');

  // NEW: year filter (client-side)
  const [year, setYear] = useState('all'); // 'all' | '2025' | '2024' | ...
  const [search, setSearch] = useState('');

  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(()=>new Set());
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(null); // { done, total } while sending

  // modal
  const [modal, setModal] = useState({ show: false, title: '', body: '' });
  const closeModal = () => setModal({ show:false, title:'', body:'' });

  const load = async () => {
    setLoading(true); setErr('');
    try {
      const data = await api.get(`/bookings/emails?filter=${encodeURIComponent(filter)}`);
      setRows(data||[]);
      setSel(new Set());      // clear selection on (re)load
      setYear('all');         // reset year filter on (re)load
    } catch(e) {
      setErr(String(e?.message||e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(()=>{ load(); /* eslint-disable-next-line */ }, [filter, refreshKey]);

  // --- derive years present from loaded rows (based on lastBooking) ---
  const years = useMemo(() => {
    const ys = new Set();
    for (const r of rows) {
      const dt = r?.lastBooking ? new Date(r.lastBooking) : null;
      if (dt && !isNaN(dt)) ys.add(String(dt.getFullYear()));
    }
    return Array.from(ys).sort((a,b)=>Number(b)-Number(a));
  }, [rows]);

  // --- apply client-side year filter to rows for display ---
  const visibleRows = useMemo(() => {
    return rows.filter(r => {
      if (year !== 'all') {
        const dt = r?.lastBooking ? new Date(r.lastBooking) : null;
        if (!(dt && !isNaN(dt) && String(dt.getFullYear()) === year)) return false;
      }
      return matchesSearch(r, search);
    });
  }, [rows, year, search]);

  // selection counts and helpers (operate on *visible* rows)
  const allChecked = visibleRows.length>0 && visibleRows.every(r => sel.has(r.email));
  const toggle = (email) => {
    const next = new Set(sel);
    if (next.has(email)) next.delete(email); else next.add(email);
    setSel(next);
  };
  const toggleAll = () => {
    if (allChecked) {
      // unselect visible
      const next = new Set(sel);
      for (const r of visibleRows) next.delete(r.email);
      setSel(next);
    } else {
      // select visible (keep any previously selected from other years)
      const next = new Set(sel);
      for (const r of visibleRows) next.add(r.email);
      setSel(next);
    }
  };

const send = async () => {
  const emails = Array.from(sel);

  setSending(true); setErr('');
  setProgress({ done: 0, total: emails.length });

  let result;
  try {
    // 1) register the mailing (subject as name) with all selected recipients as "offen"; the text is not stored
    const { id: campaignId } = await api.post('/mailings', { subject, emails });
    // 2) send in batches; every result is stored in the campaign
    result = await sendCampaignInBatches(api, { campaignId, subject, htmlBody, emails, onProgress: setProgress });
  } catch (e) {
    setModal({ show: true, title: 'Senden fehlgeschlagen', body: String(e?.message || e) });
    return;
  } finally {
    setSending(false);
    setProgress(null);
  }

  setSel(new Set());

  const ok = !result.failed.length && !result.stopped && !result.abortError;
  setModal({
    show: true,
    title: ok ? 'E-Mail gesendet' : (result.sent ? 'Teilweise gesendet' : 'Nicht gesendet'),
    body: describeSendResult(result),
  });
};

  return (
    <div style={{ position: 'relative' }}>
    {/* BUSY OVERLAY */}
    {sending && (
      <div
        aria-live="polite"
        aria-busy="true"
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(255,255,255,.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
          backdropFilter: 'blur(1px)',
          WebkitBackdropFilter: 'blur(1px)'
        }}
      >
        <div className="text-center">
          <div className="spinner-border brand-spinner" role="status" />
          <div className="small mt-2 text-muted">
            Sende E-Mails… bitte warten{progress ? ` (${progress.done} / ${progress.total})` : ''}
          </div>
        </div>
      </div>
    )}
      <div>
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <label className="form-label m-0 me-2">Filter</label>
          <select className="form-select" style={{maxWidth:220}} value={filter} onChange={e=>setFilter(e.target.value)}>
            <option value="all">Alle</option>
            <option value="subscribed">Nur Opt-in</option>
            <option value="unsubscribed">Ohne Opt-in</option>
          </select>

          {/* NEW: Year filter (only shows years that exist in loaded data) */}
          <label className="form-label m-0 ms-3 me-2">Jahr</label>
          <select
            className="form-select"
            style={{maxWidth:160}}
            value={year}
            onChange={(e)=>{ setYear(e.target.value); setSel(new Set()); /* clear selection on year change */ }}
          >
            <option value="all">Alle Jahre</option>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>

          <SearchInput value={search} onChange={setSearch} />

          <div className="ms-auto small text-muted">
            Sichtbar: {visibleRows.length} • Gesamt: {rows.length}
          </div>
        </div>

        {err && <div className="alert alert-danger">{err}</div>}

        <div className="table-responsive" style={{maxHeight: 380, overflowY:'auto', border:'1px solid #eee', borderRadius:8}}>
          {loading ? <Spinner/> : (
            <table className="table table-sm align-middle m-0">
              <thead style={{position:'sticky', top:0, background:'#fff', zIndex:1}}>
                <tr>
                  <th style={{width:32}}>
                    <input type="checkbox" checked={allChecked} onChange={toggleAll} />
                  </th>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Opt-in</th>
                  <th>Letzte Buchung</th>
                  <th>Anzahl Buchungen</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map(r=>(
                  <tr key={r.email}>
                    <td><input type="checkbox" checked={sel.has(r.email)} onChange={()=>toggle(r.email)} /></td>
                    <td>{r.email}</td>
                    <td>{[r.firstName, r.lastName].filter(Boolean).join(' ')}</td>
                    <td>{r.subscribed ? '✓' : ''}</td>
                    <td>
                      {new Date(r.lastBooking).toLocaleString("de-DE", {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                    <td>{r.totalBookings}</td>
                  </tr>
                ))}
                {!visibleRows.length && (
                  <tr><td colSpan="6" className="text-center text-muted py-4">
                    {search.trim() && rows.length ? 'Keine Treffer für diese Suche.' : 'Keine Empfänger vorhanden.'}
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        <div className="mt-3">
          <div className="mb-2 small text-muted">
            Ausgewählt (gesamt): {sel.size} &nbsp;|&nbsp; Sichtbar: {visibleRows.length}
          </div>
          <div className="mb-2">
            <label className="form-label">Betreff</label>
            <input className="form-control" value={subject} onChange={e=>setSubject(e.target.value)} />
          </div>
          <div className="mb-2">
            <label className="form-label">Nachricht (HTML erlaubt)</label>
            <textarea className="form-control" rows={6} value={htmlBody} onChange={e=>setHtmlBody(e.target.value)} />
          </div>
          <button
            className="btn btn-save"
            disabled={sending || sel.size===0 || !subject.trim() || !htmlBody.trim()}
            onClick={send}
          >
            {sending
              ? (progress ? `Sende… ${progress.done} / ${progress.total}` : 'Sende…')
              : 'Senden'}
          </button>
        </div>

        {/* Modal */}
        {modal.show && (
          <>
            <div className="modal fade show" style={{display:'block'}} tabIndex="-1" role="dialog" aria-modal="true">
              <div className="modal-dialog modal-dialog-centered" role="document">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">{modal.title}</h5>
                    <button type="button" className="btn-close" aria-label="Close" onClick={closeModal}></button>
                  </div>
                  <div className="modal-body">
                    <p style={{ whiteSpace: 'pre-line' }}>{modal.body}</p>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-primary" onClick={closeModal}>OK</button>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-backdrop fade show" />
          </>
        )}
      </div>
    </div>
  );
}


function Spinner(){
  return (
    <div className="p-4 text-center">
      <div className="spinner-border brand-spinner" role="status" />
      <div className="small mt-2 text-muted">Lädt…</div>
    </div>
  );
}
